import { RECOMPENSES_PATH } from "../recompenses/recompenses-entree";

/**
 * Les phrases des reglages de la regie (`index.tsx`, `cle-overlay.tsx`, `qr-overlay.tsx`, `import.tsx`). Un code
 * d'erreur n'a que sa phrase, ecrite ici : rien du serveur ni du fichier importe n'est affiche tel quel.
 *
 * Les libelles « Generer la cle » et « Regenerer la cle » sont ceux de la carte « Essai d'OBS » de la page Twitch :
 * les guides `docs/gestes/castellan-obs-essai.md` et `castellan-obs-scene.md` les citent, en changer un oblige a les
 * relire.
 */

import type { CoinQr } from "../contract";

export const REGLAGES_TITRE = "Réglages";
export const REGLAGES_RETOUR_DIRECT = "Retour au direct";
export const REGLAGES_RETOUR = "Retour aux réglages";

export type EntreeReglages = {
  readonly href: string;
  readonly titre: string;
  readonly description: string;
};

/** Les pages de reglages, dans l'ordre de l'index. Les lots suivants y ajouteront les leurs. */
export const ENTREES_REGLAGES: readonly EntreeReglages[] = [
  {
    href: "/regie/reglages/twitch",
    titre: "Twitch",
    description: "Les comptes de la chaîne et du bot, la liaison, l'essai d'OBS et l'agent du PC.",
  },
  {
    href: RECOMPENSES_PATH,
    titre: "Récompenses de points",
    description:
      "Toutes les récompenses de la chaîne : lesquelles Castellan règle, lesquelles lui échappent, et ce qu'il reste de places.",
  },
  {
    href: "/regie/reglages/overlays",
    titre: "Overlays d'OBS",
    description:
      "La clé des overlays et l'adresse de chacun, à coller dans une source navigateur, et le QR code du stream.",
  },
  {
    href: "/regie/reglages/import",
    titre: "Import de l'ancien Castellan",
    description: "Verser l'historique des viewers de l'ancienne base.",
  },
  {
    href: "/regie/planning",
    titre: "Planning",
    description: "La semaine de stream, l'affiche qui se compose et les images à poster.",
  },
  {
    href: "/regie/diffusion",
    titre: "Diffusion",
    description: "La semaine, le stock, le suivi des envois et les réseaux.",
  },
];

// --- Les overlays d'OBS ---------------------------------------------------------------------------------

export const OVERLAYS_TITRE = "Overlays d'OBS";
export const OVERLAYS_CLE_TITRE = "La clé des overlays";
export const OVERLAYS_INTRO =
  "Une seule clé pour tous les overlays. Elle ne s'affiche qu'une fois, juste après sa génération : colle les adresses dans OBS tout de suite. Si tu la régénères, il faudra recoller chaque overlay.";
export const OVERLAYS_CHARGEMENT = "Lecture de la clé...";
export const OVERLAYS_LECTURE_ECHEC =
  "L'état de la clé n'a pas pu être lu. Réessaie dans un instant.";
export const OVERLAYS_CLE_ABSENTE = "Aucune clé d'overlay n'est posée.";
export const OVERLAYS_GENERER = "Générer la clé";
export const OVERLAYS_REGENERER = "Régénérer la clé";
export const OVERLAYS_CONFIRMATION =
  "Régénérer remplace la clé : tous les overlays d'OBS qui l'utilisent cesseront de marcher, leurs flux sont coupés tout de suite, et il faudra recoller la nouvelle adresse dans chacune de leurs sources.";
export const OVERLAYS_CONFIRMER = "Régénérer quand même";
export const OVERLAYS_ANNULER = "Annuler";
export const OVERLAYS_GENERATION = "Génération en cours...";
export const OVERLAYS_GENERATION_ECHEC =
  "La clé n'a pas pu être générée. Réessaie dans un instant.";
export const OVERLAYS_ADRESSES_TITRE = "Adresses à coller dans OBS";
export const OVERLAYS_ADRESSES_NOTE =
  "Montrées cette fois seulement : quitter la page les efface, et la base n'a que l'empreinte de la clé. Dans OBS : source navigateur, permission « lecture des informations de l'utilisateur », audio contrôlé par OBS.";
export const OVERLAYS_COPIER = "Copier";
export const OVERLAYS_COPIEE = "Copiée";
export const OVERLAYS_COPIE_REFUSEE =
  "Le navigateur a refusé la copie : l'adresse est sélectionnée, copie-la à la main (Ctrl+C).";

/** Les overlays qui existent, avec leur chemin. Un overlay de plus s'ajoute ici et nulle part ailleurs. */
export const OVERLAYS: readonly { readonly nom: string; readonly chemin: string }[] = [
  { nom: "scène", chemin: "/overlay/scene" },
  { nom: "taverne", chemin: "/overlay/taverne" },
  { nom: "essai", chemin: "/overlay/essai" },
];

export function overlayAdresse(origine: string, chemin: string, cle: string): string {
  return `${origine}${chemin}#cle=${cle}`;
}

export function overlayCopierLabel(nom: string): string {
  return `${OVERLAYS_COPIER} l'adresse de l'overlay ${nom}`;
}

export function overlayAdresseLabel(nom: string): string {
  return `Adresse de l'overlay ${nom}`;
}

export function overlaysCleDepuis(depuis: string | null): string {
  return depuis === null
    ? "Une clé d'overlay est posée."
    : `Une clé d'overlay est posée depuis le ${depuis}.`;
}

// --- Le QR code du stream ------------------------------------------------------------------------------

export const QR_TITRE = "Le QR code du stream";
export const QR_INTRO =
  "Le QR code mène les viewers à la page « Rejoindre le château ». Il se pose dans un coin de l'écran du stream, sur l'overlay « taverne ». Il part éteint : rien n'apparaît à l'écran tant que tu ne l'allumes pas.";
export const QR_CHARGEMENT = "Lecture du réglage...";
export const QR_LECTURE_ECHEC =
  "Le réglage du QR code n'a pas pu être lu. Réessaie dans un instant.";
export const QR_INTERRUPTEUR = "Montrer le QR code sur le stream";
export const QR_ALLUME = "Allumé";
export const QR_ETEINT = "Éteint";
export const QR_COIN_LEGENDE = "Coin de l'écran";
export const QR_ENREGISTREMENT = "Enregistrement...";
export const QR_ENREGISTRE = "Enregistré : les overlays ouverts suivent tout de suite.";
export const QR_ECRITURE_ECHEC =
  "Le réglage n'a pas été enregistré. L'état affiché est celui que garde le serveur : réessaie dans un instant.";
export const QR_ECRITURE_INCERTAINE =
  "Le serveur n'a pas répondu à temps : le réglage a peut-être été enregistré. L'état affiché est celui que le serveur garde.";
export const QR_ECRITURE_SESSION =
  "La session a expiré : recharge la page et reconnecte-toi à la régie, puis recommence.";
export const QR_NOTE_OBS =
  "Pour l'ajouter dans OBS : prends l'adresse de la source navigateur « scene » et remplace scene par taverne, le reste ne change pas (c'est la même clé). Il n'y a rien à régénérer : régénérer couperait « scene ». Règle la source à la taille du canevas (1920 x 1080) : le code s'affiche alors à sa taille exacte, 205 px, celle que les lecteurs de QR code lisent le mieux.";

/** Les coins de l'écran, en toutes lettres, dans l'ordre de l'écran (haut puis bas, gauche puis droit). */
export const COINS_LIBELLES: Readonly<Record<CoinQr, string>> = {
  "haut-gauche": "Haut gauche",
  "haut-droit": "Haut droit",
  "bas-gauche": "Bas gauche",
  "bas-droit": "Bas droit",
};

/** Ce que dit l'ecran du coin choisi, en une phrase (le choix ne tient pas qu'a la couleur). */
export function qrCoinChoisi(coin: CoinQr, visible: boolean): string {
  const ou = COINS_LIBELLES[coin].toLowerCase();
  return visible
    ? `Le QR code est allumé, coin ${ou}.`
    : `Le QR code est éteint. Coin choisi : ${ou}.`;
}

// --- L'import de l'ancien Castellan ---------------------------------------------------------------------

export const IMPORT_TITRE = "Import de l'ancien Castellan";
export const IMPORT_PANNEAU_TITRE = "Le fichier d'export";
export const IMPORT_INTRO =
  "Le fichier sort du script d'export lancé sur le PC. Il contient les viewers, leurs premières venues, leurs streams, leurs bits, leurs messages comptés et le texte de ces messages, que la fiche d'un viewer montre ensuite. Les bots et les viewers qui ont demandé l'effacement sont ignorés. Le renvoyer ne double rien.";
export const IMPORT_FICHIER_LABEL = "Fichier d'export";
export const IMPORT_FICHIER_NOTE =
  "Un fichier .json à la fois ; s'il y en a plusieurs (-1, -2), envoie-les l'un après l'autre.";
export const IMPORT_LECTURE = "Lecture du fichier...";
export const IMPORT_FICHIER_INVALIDE =
  "Ce fichier n'est pas un export valide de l'ancien Castellan. Rien n'a été envoyé.";
export const IMPORT_FICHIER_TROP_LOURD =
  "Ce fichier est trop lourd pour un envoi (2 Mo au plus). Rien n'a été envoyé : coupe l'export en plusieurs fichiers.";
export const IMPORT_IMPORTER = "Importer";
export const IMPORT_ENVOI = "Envoi en cours...";
export const IMPORT_RESULTATS_TITRE = "Fichiers importés";

/** Ce que le fichier contient, avant l'envoi : des nombres, jamais un pseudo ni un texte. */
export function importApercu(nom: string, viewers: number, messages: number): string {
  const v = `${viewers} viewer${viewers > 1 ? "s" : ""}`;
  const m = `${messages} message${messages > 1 ? "s" : ""}`;
  return `${nom} : ${v} et ${m}, prêts à être envoyés.`;
}

export function importResultat(
  nom: string,
  r: { importes: number; ignores: number; messages: number },
): string {
  const importes = `${r.importes} viewer${r.importes > 1 ? "s" : ""} importé${r.importes > 1 ? "s" : ""}`;
  const ignores = `${r.ignores} ignoré${r.ignores > 1 ? "s" : ""} (bots et oppositions)`;
  const messages = `${r.messages} message${r.messages > 1 ? "s" : ""} écrit${r.messages > 1 ? "s" : ""}`;
  return `${nom} : ${importes}, ${ignores}, ${messages}.`;
}

/** Une phrase par statut d'echec de l'envoi. Les autres statuts prennent `IMPORT_ECHEC`. */
export const IMPORT_ECHEC_PAR_STATUT: Readonly<Record<number, string>> = {
  400: "Le serveur a refusé ce fichier (hors format). Rien n'a été écrit.",
  401: "La session a expiré : reconnecte-toi à la régie, puis renvoie le fichier.",
  403: "Le serveur refuse cet envoi. Recharge la régie et reconnecte-toi, puis renvoie le fichier.",
  413: "Le serveur refuse ce fichier : il est trop lourd. Coupe l'export en plusieurs fichiers.",
  503: "La base n'a pas répondu. Rien n'a été écrit : réessaie dans un instant.",
};
export const IMPORT_ECHEC = "L'envoi n'a pas abouti. Réessaie dans un instant.";
